import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import Icon from '../../components/Icon.jsx'
import Loadable from '../../components/Loadable.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import UtgNav from './components/UtgNav'
import UtgTools from './components/UtgTools.jsx'

import { fetchComponentConfig, fetchDeviceConfigRecordsList, updateComponentConfig } from '../../state/actions/componentConfigActions.js'
import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions.js'
import { fetchUtg, fetchUtgDeviceList } from '../../state/actions/utgActions.js'

import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal, ccDeviceConfigSignal, deviceConfigRecordsListSignal, deviceConfigRecordsListLoadedSignal } from '../../state/signals/componentConfigSignals.js'
import { schemaManifestSignal, schemaManifestLoadedSignal, schemaManifestErrorSignal } from '../../state/signals/schemaManifestSignals.js'
import { utgSignal, utgLoadedSignal, utgDeviceListSignal, utgDeviceListLoadedSignal, utgDeviceListErrorSignal } from '../../state/signals/utgSignals.js'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
export default function UtgDeviceList() {

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, componentId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(() => {
        if (beIdChanged) return
        fetchUtg(beId, componentId, config, accessToken).then(
            (utg) => {
                fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
            }
        )
        fetchSchemaManifest()
        fetchUtgDeviceList(beId, componentId, config, accessToken)
        fetchDeviceConfigRecordsList(beId, componentId, config, accessToken)
    }, [beId, beIdChanged, componentId, config, accessToken])

    const devicesComputed = useComputed(() => {
        // devices is a subset of componentConfig
        if (componentConfigSignal.value) {
            console.log(componentConfigSignal.value)
            return componentConfigSignal.value.config?.deviceConfig?.device ?? []
        }
        return []
    })

    const showDeleteModalSignal = useSignal(false)
    const confirmDeviceTypeSignal = useSignal('')
    const confirmTaskDescSignal = useSignal('')
    const accordionData = [
        { name: 'Device configs', data: devicesComputed.value },
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Component Config Data', data: componentConfigSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value },
        { name: 'UTG devices', data: utgDeviceListSignal.value }
    ]

    function DeviceTableRows({ devices }) {
        if (!utgDeviceListLoadedSignal.value) {
            return <tr><td colSpan={4}><LoadingAnimation /></td></tr>
        }
        if (devices && devices.length == 0) {
            return (
                <tr><td colSpan="4" className="text-center">This UTG does not contain any configured devices</td></tr>
            )
        }

        let rows = []
        devices.forEach(device => {
            rows.push(
                <DeviceRow device={device} key={`${device.deviceType}${device.taskDesc}`} />
            )
        })

        return rows
    }

    function DeviceRow({ device }) {
        let deviceType = device.deviceType
        let taskDesc = device.taskDesc
        let encodedDeviceType = encodeURIComponent(deviceType)
        return (
            <tr>
                <td><Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>{device.deviceType}</Link></td>
                <td>{device.taskDesc}</td>
                <td>{device.threadType}</td>
                <td>
                    <Link to={`./${encodedDeviceType}?desc=${taskDesc}`}><Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>{' '}
                    <Button variant="outline-danger" onClick={() => showModal(deviceType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>
                </td>
            </tr>
        )
    }

    const deleteDevice = () => {
        showDeleteModalSignal.value = false

        // filter out device to be deleted
        const unfilteredDevices = ccDeviceConfigSignal.value.device
        const filteredDevices = unfilteredDevices.filter(device =>
            !(device.deviceType == confirmDeviceTypeSignal.value && device.taskDesc == confirmTaskDescSignal.value));

        // set newly filtered device array in componentConfigData    
        componentConfigSignal.value.config.deviceConfig.device = filteredDevices

        //build data object for updateComponentConfig request
        let data = {
            config: componentConfigSignal.value.config,
            hash: componentConfigSignal.value.hash,
            isCommitted: componentConfigSignal.value.isCommitted,
            lastUpdatedAt: componentConfigSignal.value.lastUpdatedAt,
            lastUpdatedBy: componentConfigSignal.value.lastUpdatedBy,
            revisionId: componentConfigSignal.value.revisionId,
            componentId: componentConfigSignal.value.componentId
        }

        //updates componentConfig with device removed
        updateComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, data, config, accessToken)
            .then(() => {
                //console.log('successfully updated component config')
                addNotification('The Device was successfully removed.', 'success', true)
                componentConfigSignal.value = data
                window.location.reload()
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString())
                return false
            })
            .finally(() => {
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (deviceType, taskDesc) => {
        // just set the state and the modal will appear
        confirmDeviceTypeSignal.value = deviceType
        confirmTaskDescSignal.value = taskDesc
        showDeleteModalSignal.value = true
    }

    function NewDeviceDropdown() {
        if (schemaManifestSignal && schemaManifestSignal.value && schemaManifestSignal.value['device-configs'].length) {
            let deviceOptions = schemaManifestSignal.value['device-configs'].map(s => {
                let escapedName = encodeURIComponent(s.name)
                return (
                    <Dropdown.Item href={`/be/${beId}/utgs/${componentId}/devices/${escapedName}`} key={s.name}>{s.name}</Dropdown.Item>
                )
            })

            return (
                <DropdownButton id="dropdown-add-device"
                    variant="outline-secondary"
                    title="New Device"
                    className="mx-3">
                    {deviceOptions}
                </DropdownButton>
            )
        }
    }

    return (
        <>
            {
                userPermissionsSummary?.value?.canEditUtgs && (
                    <ConfirmationModal
                        show={showDeleteModalSignal.value}
                        handleCancel={
                            () => showDeleteModalSignal.value = false
                        }
                        handleConfirm={deleteDevice}
                        message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                        title={`Deleting ${confirmTaskDescSignal.value}`} />
                )
            }
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? <>Manage {utgSignal.value.componentNickname}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='UTG Devices' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[componentConfigLoadedSignal]} errorSignals={[componentConfigErrorSignal]}>
                    <UtgTools componentConfig={componentConfigSignal.value} canCreateProfile={userPermissionsSummary?.value?.canEditProfiles} beId={beId} config={config} accessToken={accessToken}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="3"></UtgNav>
            <Table responsive striped hover size="sm">
                <thead>
                    <tr>
                        <th>Device Type</th>
                        <th>Device Description</th>
                        <th>Thread Type</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody><DeviceTableRows devices={devicesComputed.value} /></tbody>
            </Table>
            {
                userPermissionsSummary?.value?.canEditUtgs && <NewDeviceDropdown />
            }
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}